// Schemes a CMS-defined action link is allowed to use. Anything else — most
// importantly javascript: and data: — is treated as no link at all.
const SAFE_SCHEMES = ["https:", "http:", "tel:", "mailto:"]

/**
 * Resolves the CMS-defined destination for the model page action buttons
 * ("תיאום פגישה באולם תצוגה" / "הזמנת נסיעת הדגמה").
 *
 * The CMS fields (showroomMeetingUrl / driveTestUrl on CarModelMainDto) are
 * optional per model. Per DLK-885, when a field is empty the button must keep
 * its current default behavior — so this returns null to signal "no CMS link,
 * fall back to the existing flow".
 *
 * @param cmsUrl Raw value straight from the CMS field. May be undefined, empty,
 *               padded with whitespace, an absolute URL, or a site-relative path.
 * @returns The href to navigate to, or null to preserve the default behavior.
 */
export const resolveCmsActionLink = (cmsUrl?: string): string | null => {
  // Editors routinely leave trailing whitespace, so a padded value must still
  // count as empty and fall back to the default behavior.
  const url = cmsUrl?.trim()
  if (!url) return null

  // A leading slash marks a site-relative path (e.g. /meeting/5). Prefixing a
  // protocol here would break it, so it is used as-is.
  if (url.startsWith("/")) return url

  // A value carrying its own scheme is only navigable if that scheme is one we
  // allow. The href reaches the DOM as an anchor target, so an unrestricted
  // scheme would let a CMS value such as "javascript:..." execute on the page.
  // Anything outside the allowlist falls back to the button default.
  try {
    const parsed = new URL(url)
    return SAFE_SCHEMES.includes(parsed.protocol.toLowerCase())
      ? parsed.toString()
      : null
  } catch {
    // No parseable scheme — handled below as a bare host.
  }

  // Anything left is a bare host like www.mazda.co.il/x, which would otherwise
  // resolve against the current model page and 404.
  return `https://${url}`
}
